import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Calculator, RotateCcw, CreditCard, HandCoins, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MANUAL_METHODS = ["Cash", "Cash App", "Zelle", "Square", "Apple Pay", "Google Pay", "Check", "Other"];
const inputCls = "w-full bg-transparent border-b border-white/15 focus:border-[#F2B705] outline-none py-1.5 text-sm rounded-none";

export default function RefundDialog({ booking, token, onAuthError, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selId, setSelId] = useState(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Cash");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [sendReceipt, setSendReceipt] = useState(true);
  const [showHelper, setShowHelper] = useState(false);
  const [h, setH] = useState({ rental_amount: "", total_days: "", days_used: "", unused_days: "" });
  const [suggested, setSuggested] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, [booking.id]);

  const reload = () => {
    axios.get(`${API}/bookings/${booking.id}/refundable-payments`, auth)
      .then((r) => {
        setData(r.data);
        const first = (r.data.payments || []).find((p) => p.refundable > 0.009);
        if (first) setSelId((prev) => prev || first.id);
        setH((prev) => (prev.rental_amount ? prev : {
          rental_amount: r.data.booking?.rental_amount || "",
          total_days: r.data.booking?.days || "",
          days_used: "", unused_days: "",
        }));
      })
      .catch((e) => { if (e.response?.status === 401) onAuthError?.(); else toast.error("Could not load payments."); })
      .finally(() => setLoading(false));
  };

  const voidRefundEntry = async (r) => {
    if (!window.confirm(`Mark refund ${r.number} (${money(r.amount)}) as a VOIDED ENTRY ERROR?\n\nUse this ONLY when no money was actually returned to the customer — the refund was a data-entry mistake.\n\nThe record stays permanently visible, but it stops counting in Money Out, Net Profit and balances.`)) return;
    const reason = window.prompt("Reason this refund entry is an error (required — written to the audit trail):");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required."); return; }
    try {
      await axios.post(`${API}/refunds/${r.id}/void-entry`, { reason: reason.trim() }, auth);
      toast.success(`Refund ${r.number} marked VOIDED ENTRY ERROR — NOT COUNTED.`);
      reload(); onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not void the refund entry."); }
  };

  const undoRefundVoid = async (r) => {
    if (!window.confirm(`Undo the void on refund ${r.number} (${money(r.amount)})? It will count as a real refund again.`)) return;
    const reason = window.prompt("Reason for undoing the void (required):");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required."); return; }
    try {
      await axios.post(`${API}/refunds/${r.id}/undo-void`, { reason: reason.trim() }, auth);
      toast.success(`Void undone — refund ${r.number} counts again.`);
      reload(); onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not undo the void."); }
  };

  const sel = useMemo(() => (data?.payments || []).find((p) => p.id === selId), [data, selId]);
  const isStripe = !!sel?.stripe;

  const calc = useMemo(() => {
    const total = parseFloat(h.rental_amount) || 0;
    const days = parseInt(h.total_days) || 0;
    const used = h.days_used === "" ? null : parseInt(h.days_used) || 0;
    const unused = h.unused_days !== "" ? parseInt(h.unused_days) || 0 : (used !== null && days ? Math.max(0, days - used) : 0);
    if (!total || !days || !unused) return null;
    const perDay = total / days;
    const sug = Math.round(perDay * unused * 100) / 100;
    return { perDay, unused, sug, math: `${money(total)} ÷ ${days} days = ${money(perDay)}/day × ${unused} unused day(s) = ${money(sug)} suggested refund` };
  }, [h]);

  const amt = parseFloat(amount) || 0;
  const reasonRequired = suggested !== null && Math.abs(amt - suggested) > 0.01;

  const setHf = (k, v) => setH((p) => ({ ...p, [k]: v }));

  const useSuggested = () => {
    if (!calc) return;
    setAmount(String(calc.sug));
    setSuggested(calc.sug);
    toast.success("Suggested refund applied — you can still adjust it.");
  };

  const submit = async () => {
    if (!sel) { toast.error("Select the original payment to refund."); return; }
    if (amt <= 0) { toast.error("Enter a refund amount greater than $0."); return; }
    if (amt > sel.refundable + 0.005) { toast.error(`Only ${money(sel.refundable)} is refundable on this payment.`); return; }
    if (reasonRequired && !reason.trim()) { toast.error("A reason is required since the amount differs from the suggested refund."); return; }
    setSubmitting(true);
    try {
      const payload = {
        payment_id: sel.id, amount: amt, method: isStripe ? "Card (Stripe)" : method,
        reason, notes, refund_date: date, send_receipt: sendReceipt,
        helper: suggested !== null ? {
          rental_amount: parseFloat(h.rental_amount) || 0, total_days: parseInt(h.total_days) || 0,
          days_used: parseInt(h.days_used) || 0, unused_days: calc?.unused || 0, suggested,
        } : null,
      };
      const { data: res } = await axios.post(`${API}/bookings/${booking.id}/refunds`, payload, auth);
      toast.success(isStripe
        ? `Stripe refund of ${money(amt)} submitted — card refunds take 5–10 business days.`
        : `Manual refund of ${money(amt)} recorded.`);
      if (res.receipt_results) {
        const rr = res.receipt_results;
        toast.info(`Receipt — SMS: ${rr.sms} · Email: ${rr.email}`);
      }
      onDone?.();
      onClose?.();
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error(err.response?.data?.detail || "Could not process the refund.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm p-4" data-testid="refund-dialog">
      <div className="w-full max-w-2xl bg-[#0C0C0C] border border-white/15 rounded-xl my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2 font-display font-bold text-lg"><RotateCcw size={18} className="text-[#F2B705]" /> Record Refund — {booking.name}</div>
          <button data-testid="refund-close" onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>

        {loading ? <div className="flex justify-center py-14"><Loader2 className="animate-spin text-[#F2B705]" /></div> : (
          <div className="p-5 space-y-5">
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="text-white/50">Net amount paid: <b className="text-white">{money(data?.booking?.amount_paid)}</b></span>
              {data?.booking?.total_refunded > 0 && <span className="text-white/50">Refunded to date: <b className="text-red-300">{money(data?.booking?.total_refunded)}</b></span>}
            </div>

            {/* Original payment picker */}
            <div>
              <div className="text-[11px] uppercase tracking-widest text-white/40 mb-2">1 · Select the original payment</div>
              {(data?.payments || []).length === 0 && <p className="text-white/40 text-sm">No recorded payments found for this booking.</p>}
              <div className="space-y-1.5 max-h-44 overflow-y-auto">
                {(data?.payments || []).map((p) => (
                  <label key={p.id} data-testid={`refund-payment-${p.id}`}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer text-sm ${selId === p.id ? "border-[#F2B705]/60 bg-[#F2B705]/5" : "border-white/10 hover:border-white/25"} ${p.refundable <= 0.009 ? "opacity-40" : ""}`}>
                    <input type="radio" name="refund-payment" checked={selId === p.id} disabled={p.refundable <= 0.009}
                      onChange={() => setSelId(p.id)} className="accent-[#F2B705]" />
                    {p.stripe ? <CreditCard size={15} className="text-blue-300 shrink-0" /> : <HandCoins size={15} className="text-green-400 shrink-0" />}
                    <span className="min-w-0 flex-1">
                      <span className="text-white/85">{p.label}</span>
                      <span className="text-white/40 text-xs block truncate">{(p.paid_at || "").slice(0, 10)}{p.note ? ` · ${p.note}` : ""}</span>
                    </span>
                    <span className="text-right shrink-0">
                      <span className="font-semibold text-white/85">{money(p.amount)}</span>
                      {p.refunded > 0 && <span className="block text-[11px] text-red-300">−{money(p.refunded)} refunded</span>}
                    </span>
                  </label>
                ))}
              </div>
              {sel && (
                <p className={`text-xs mt-2 ${isStripe ? "text-blue-300" : "text-white/45"}`}>
                  {isStripe
                    ? "This payment was made through Stripe — the refund will be sent back to the customer's card automatically (5–10 business days)."
                    : "This payment was made outside Stripe — record how you returned the money below."}
                </p>
              )}
            </div>

            {/* Unused days helper */}
            <div className="rounded-md border border-white/10 bg-[#0A0A0A]">
              <button data-testid="refund-helper-toggle" onClick={() => setShowHelper((v) => !v)}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-white/70 hover:text-[#F2B705] transition-colors">
                <Calculator size={15} /> Calculate Unused Days {showHelper ? "▾" : "▸"}
              </button>
              {showHelper && (
                <div className="px-3 pb-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div><label className="text-[11px] text-white/45">Rental amount $</label>
                      <input data-testid="refund-helper-amount" type="number" className={inputCls} value={h.rental_amount} onChange={(e) => setHf("rental_amount", e.target.value)} /></div>
                    <div><label className="text-[11px] text-white/45">Total rental days</label>
                      <input data-testid="refund-helper-days" type="number" className={inputCls} value={h.total_days} onChange={(e) => setHf("total_days", e.target.value)} /></div>
                    <div><label className="text-[11px] text-white/45">Days used</label>
                      <input data-testid="refund-helper-used" type="number" className={inputCls} value={h.days_used} onChange={(e) => { setHf("days_used", e.target.value); setHf("unused_days", ""); }} /></div>
                    <div><label className="text-[11px] text-white/45">Unused days</label>
                      <input data-testid="refund-helper-unused" type="number" className={inputCls}
                        value={h.unused_days !== "" ? h.unused_days : (calc?.unused ?? "")} onChange={(e) => setHf("unused_days", e.target.value)} /></div>
                  </div>
                  {calc && (
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <span data-testid="refund-helper-math" className="text-[#F2B705] text-sm font-semibold">{calc.math}</span>
                      <button data-testid="refund-use-suggested" onClick={useSuggested}
                        className="bg-[#F2B705] text-black text-xs font-bold px-3 py-1.5 rounded-md hover:bg-white transition-colors">Use {money(calc.sug)}</button>
                    </div>
                  )}
                  <p className="text-white/35 text-[11px] mt-2">Rental time only — coverage, insurance, waivers, fees and add-ons are NOT included. Enter the rental-only amount above.</p>
                </div>
              )}
            </div>

            {/* Amount + method + details */}
            <div>
              <div className="text-[11px] uppercase tracking-widest text-white/40 mb-2">2 · Refund details</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div><label className="text-[11px] text-white/45">Refund amount $ {sel && <span className="text-white/30">(max {money(sel.refundable)})</span>}</label>
                  <input data-testid="refund-amount" type="number" className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
                {!isStripe && (
                  <div><label className="text-[11px] text-white/45">Refund method</label>
                    <select data-testid="refund-method" className={`${inputCls} appearance-none`} value={method} onChange={(e) => setMethod(e.target.value)}>
                      {MANUAL_METHODS.map((m) => <option key={m} value={m} className="bg-[#0A0A0A]">{m}</option>)}
                    </select></div>
                )}
                <div><label className="text-[11px] text-white/45">Refund date</label>
                  <input data-testid="refund-date" type="date" className={`${inputCls} [color-scheme:dark]`} value={date} onChange={(e) => setDate(e.target.value)} /></div>
                <div className="col-span-2 sm:col-span-3"><label className="text-[11px] text-white/45">Reason {reasonRequired && <span className="text-red-400 font-bold">— required (amount differs from suggested)</span>}</label>
                  <input data-testid="refund-reason" className={inputCls} placeholder="e.g. Mechanical breakdown — early return" value={reason} onChange={(e) => setReason(e.target.value)} /></div>
                <div className="col-span-2 sm:col-span-3"><label className="text-[11px] text-white/45">Notes (internal)</label>
                  <input data-testid="refund-notes" className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-white/75 cursor-pointer">
              <input data-testid="refund-send-receipt" type="checkbox" checked={sendReceipt} onChange={(e) => setSendReceipt(e.target.checked)} className="accent-[#F2B705]" />
              Send refund receipt to customer by SMS and email
            </label>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button onClick={onClose} className="border border-white/15 text-white/70 text-xs font-bold px-4 py-2.5 rounded-md hover:bg-white/5">Cancel</button>
              <button data-testid="refund-submit" onClick={submit} disabled={submitting || !sel}
                className="bg-[#F2B705] text-black text-xs font-bold px-5 py-2.5 rounded-md hover:bg-white transition-colors flex items-center gap-1.5 disabled:opacity-50">
                {submitting ? <Loader2 className="animate-spin" size={14} /> : <RotateCcw size={14} />}
                {isStripe ? `Refund ${amt > 0 ? money(amt) : ""} to card via Stripe` : `Record ${amt > 0 ? money(amt) : ""} refund`}
              </button>
            </div>

            {/* History */}
            {(data?.refunds || []).length > 0 && (
              <div className="border-t border-white/10 pt-3">
                <div className="text-[11px] uppercase tracking-widest text-white/40 mb-2">Refund history</div>
                <div className="space-y-1.5 max-h-36 overflow-y-auto">
                  {data.refunds.map((r) => (
                    <div key={r.id} data-testid={`refund-history-${r.id}`} className={`text-xs rounded px-3 py-2 border ${r.entry_voided ? "border-red-500/25 bg-red-500/[0.04]" : "bg-[#0A0A0A] border-white/10"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`min-w-0 truncate ${r.entry_voided ? "text-white/40" : "text-white/70"}`}>{r.number} · {r.refund_date} · {r.method}{r.reason ? ` — ${r.reason}` : ""}</span>
                        <span className="flex items-center gap-1.5 shrink-0">
                          {r.entry_voided && <span data-testid={`refund-voided-badge-${r.id}`} className="text-[9px] font-bold text-red-300 border border-red-500/40 rounded px-1.5 py-0.5">VOIDED ENTRY ERROR — NOT COUNTED</span>}
                          <span className={`font-semibold ${r.entry_voided ? "text-white/35 line-through" : "text-red-300"}`}>−{money(r.amount)}</span>
                        </span>
                      </div>
                      {r.entry_voided && r.void_info && (
                        <div className="text-[10px] text-red-300/70 mt-0.5">Voided by {r.void_info.by} · {(r.void_info.at || "").slice(0, 10)} — {r.void_info.reason}. No money ever moved.</div>
                      )}
                      <div className="flex justify-end mt-1">
                        {r.workflow !== "stripe" && !r.entry_voided && (
                          <button data-testid={`refund-void-entry-${r.id}`} onClick={() => voidRefundEntry(r)}
                            title="Management only — no money was actually returned; this refund was a data-entry mistake"
                            className="text-[9px] font-bold text-white/40 border border-white/15 rounded px-1.5 py-0.5 hover:text-red-300 hover:border-red-400/50 transition-colors">
                            Void Entry Error
                          </button>
                        )}
                        {r.entry_voided && (
                          <button data-testid={`refund-undo-void-${r.id}`} onClick={() => undoRefundVoid(r)}
                            className="text-[9px] font-bold text-emerald-300/80 border border-emerald-500/30 rounded px-1.5 py-0.5 hover:text-emerald-200 hover:border-emerald-400/60 transition-colors">
                            Undo Void
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
